<!-- Optimized MinLivsstilPage.vue with Performance Improvements -->
<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import {
  IonContent,
  IonIcon,
  IonLoading,
  IonFab,
  IonFabButton,
  IonicSlides,
  IonPage,
  useIonRouter,
} from '@ionic/vue';
import { useStore as useMainStore } from '@/stores/main';
import { useStore as usePagesStore } from '@/stores/pages';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  provide,
  ref,
  shallowRef,
  watchEffect,
} from 'vue';
import { homeSharp } from 'ionicons/icons';
import { useBackendFetch } from '@/fetch';
import MinLivsstilStart from '@/views/slides/MinLivsstil/MinLivsstilStart.vue';
import MinLivsstilSection from '@/views/slides/MinLivsstil/MinLivsstilSection.vue';
import MinLivsstilHeader from '@/views/slides/MinLivsstil/MinLivsstilHeader.vue';
import MinLivsstilResults from '@/views/slides/MinLivsstil/MinLivsstilResults.vue';
import UnifiedNavigation from '@/views/slides/MinLivsstil/UnifiedNavigation.vue';

const mainStore = useMainStore();
const pagesStore = usePagesStore();
const router = useIonRouter();

const props = defineProps({
  page: Number,
});

// Use shallowRef for better performance with large objects
const slides = shallowRef<any>(null);
const sections = shallowRef<any[]>([]);
const sectionElements = shallowRef<any[]>([]);

// Computed values for window dimensions
const windowHeight = ref(window.innerWidth <= 445 ? window.innerHeight : 844);

// UI state tracking
const isLoading = ref(true);
const warningOpen = ref(false);
const shouldResetNavigation = ref(false);
const globalStylesheet = shallowRef<HTMLStyleElement | null>(null);

// Pre-define functions to avoid recreating them
const preventTouchMove = (e: TouchEvent) => {
  if (!(e.target as HTMLElement)?.closest('.slider-content')) {
    e.preventDefault();
  }
};

// Use a more efficient resize handler with debouncing
let resizeTimeout: number;
const updateWindowHeight = () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    windowHeight.value = window.innerWidth <= 445 ? window.innerHeight : 844;
  }, 16); // 16ms for 60fps
};

// Memoized color scheme
const colorScheme = 'green';
const backgroundColor = computed(() => {
  const colorMap = {
    purple: '#f5f3ff',
    yellow: '#fff9db',
    green: '#edf9eb',
    blue: '#e6f7ff',
  };
  return colorMap[colorScheme] || '#fff9db';
});

// Memoized current section computations
const currentSectionIdx = computed(() => slides.value?.activeIndex ?? 0);
const currentSection = computed(() => {
  const idx = currentSectionIdx.value - 1;
  return idx < 0 || idx >= sections.value.length ? null : sections.value[idx];
});

// Question tracking
const questionIndex = ref(1);
const questionCount = computed(() => currentSection.value?.groups?.length ?? 1);

// Optimized height calculation with memoization
const fullHeight = computed(() => {
  const headerHeight = (slides.value?.activeIndex ?? 0) > 0 ? 172 : 112;
  const footerHeight = 80;
  const availableHeight = window.innerHeight - headerHeight - footerHeight;

  // Simplified responsive logic
  if (window.innerWidth < 600) return availableHeight;
  return availableHeight;
});

// Lifecycle hooks
onMounted(async () => {
  // Batch DOM operations
  requestAnimationFrame(() => {
    // Add event listeners
    window.addEventListener('resize', updateWindowHeight, { passive: true });
    document.addEventListener('touchmove', preventTouchMove, {
      passive: false,
    });

    // Create and apply styles in one operation
    const stylesheet = document.createElement('style');
    stylesheet.textContent = `
      html, body {
        position: fixed;
        width: 100%;
        height: 100%;
        overflow: hidden;
        -webkit-overflow-scrolling: none;
        touch-action: none;
      }
      .ion-content-scroll-host {
        overflow: hidden !important;
        -webkit-overflow-scrolling: none !important;
        touch-action: none !important;
      }
    `;
    document.head.appendChild(stylesheet);
    globalStylesheet.value = stylesheet;

    // Apply body styles
    Object.assign(document.body.style, {
      overflow: 'hidden',
      position: 'fixed',
      width: '100%',
      height: '100%',
      touchAction: 'none',
    });
  });

  // Initialize data
  initializeVisitedSections();

  try {
    const { data, statusCode } = await useBackendFetch('/questions/life')
      .get()
      .json();

    if (statusCode.value === 401) {
      mainStore.logout();
      router.replace('/login');
      return;
    }

    sections.value = data.value.sections;

    // Initialize save points more efficiently
    mainStore.savePoints = {
      ...mainStore.savePoints,
      life: {
        ...mainStore.savePoints?.life,
        page: mainStore.savePoints?.life?.page ?? 0,
        question: mainStore.savePoints?.life?.question ?? 0,
        sections: sections.value,
      },
    };

    await nextTick();
    recalculateHeight(slides.value);
  } finally {
    isLoading.value = false;
  }
});

onUnmounted(() => {
  // Cleanup in one batch
  window.removeEventListener('resize', updateWindowHeight);
  document.removeEventListener('touchmove', preventTouchMove);

  if (globalStylesheet.value?.parentNode) {
    globalStylesheet.value.parentNode.removeChild(globalStylesheet.value);
  }

  // Reset body styles
  Object.assign(document.body.style, {
    overflow: '',
    position: '',
    width: '',
    height: '',
    touchAction: '',
  });

  // Clear timeout if exists
  clearTimeout(resizeTimeout);
});

// Optimized functions
function setSliderInstance(swiper: any) {
  slides.value = swiper;
}

function updateQuestionIndex(idx: number) {
  questionIndex.value = typeof idx === 'number' ? idx + 1 : 1;
  if (mainStore.savePoints?.life) {
    mainStore.savePoints.life.question = idx;
  }
}

function initializeVisitedSections() {
  if (!mainStore.savePoints) mainStore.savePoints = {};
  if (!mainStore.savePoints.life) mainStore.savePoints.life = {};
  if (!mainStore.savePoints.life.visitedSections) {
    mainStore.savePoints.life.visitedSections = [];
  }
}

// Optimized navigation functions
async function navigateSlide(direction: 'next' | 'prev') {
  const saveLife = mainStore.savePoints?.life || {};
  let newIndex =
    direction === 'next'
      ? currentSectionIdx.value + 1
      : currentSectionIdx.value - 1;

  // Skip the MinLivsstilEnd slide - go directly from last section to results
  if (direction === 'next' && newIndex === sections.value.length + 1) {
    newIndex = sections.value.length + 1; // Go directly to results slide (which is now at sections.length + 1)
  }

  mainStore.savePoints = {
    ...mainStore.savePoints,
    life: {
      ...saveLife,
      page: newIndex,
      question: 0,
    },
  };

  if (direction === 'next' && newIndex === sections.value.length + 1) {
    // Go directly to results slide
    slides.value.slideTo(sections.value.length + 1);
  } else {
    direction === 'next' ? slides.value.slideNext() : slides.value.slidePrev();
  }

  questionIndex.value = 1;
  shouldResetNavigation.value = true;
  recalculateHeight(slides.value, slides.value.params.speed);

  // Reset scroll position on the target section
  const targetSectionEl = sectionElements.value[newIndex - 1];
  if (targetSectionEl?.resetScroll) {
    targetSectionEl.resetScroll();
    setTimeout(() => targetSectionEl.resetScroll?.(), 200);
  }
}

const nextSlide = () => navigateSlide('next');
const previousSlide = () => {
  if (currentSectionIdx.value > 0) {
    // If we're on the results slide, go back to the last section
    if (currentSectionIdx.value === sections.value.length + 1) {
      slides.value.slideTo(sections.value.length);
      questionIndex.value = 1;
      shouldResetNavigation.value = true;
      recalculateHeight(slides.value, slides.value.params.speed);
    } else {
      navigateSlide('prev');
    }
  }
};

function setSection(idx: number) {
  if (idx < 0 || idx > slides.value.slides.length - 1) {
    console.error('Invalid section index:', idx);
    return;
  }

  mainStore.savePoints = {
    ...mainStore.savePoints,
    life: {
      ...mainStore.savePoints?.life,
      page: idx,
      question: 0,
    },
  };

  try {
    slides.value.slideTo(idx);
    questionIndex.value = 1;
    shouldResetNavigation.value = true;
    
    // Directly reset the target section's swiper to first slide
    // idx-1 because sectionElements doesn't include the start slide
    const targetSection = sectionElements.value[idx - 1];
    if (targetSection?.slideTo) {
      // Reset immediately and after delay to ensure swiper is ready
      targetSection.slideTo(0);
      setTimeout(() => targetSection.slideTo?.(0), 150);
    }
    // Reset scroll position to top for the target section
    if (targetSection?.resetScroll) {
      targetSection.resetScroll();
      setTimeout(() => targetSection.resetScroll?.(), 150);
    }
  } catch (e) {
    console.error('Error in slideTo:', e);
  }
}

// Optimized page navigation
function goToNextPage() {
  mainStore.savePoints = {
    ...mainStore.savePoints,
    life: {
      ...mainStore.savePoints?.life,
      page: 0,
      question: 0,
    },
  };

 


  // Create preloader overlay
  const preloader = document.createElement('div');
  Object.assign(preloader.style, {
    position: 'fixed', top: '0', left: '0', width: '100%', height: '100%',
    background: '#fff', zIndex: '9999', opacity: '0', transition: 'opacity 0.3s',
  });
  document.body.appendChild(preloader);

  // Animate and navigate
  requestAnimationFrame(() => {
    preloader.style.opacity = '1';

    // Prefetch next page
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = '/goals';
    document.head.appendChild(link);

    setTimeout(() => {
      router.push('/goals');
      setTimeout(() => preloader.remove(), 500);
    }, 100);
  });
}

// Optimized height recalculation
function recalculateHeight(swiper: any, timeout = 0) {
  if (!swiper?.wrapperEl) return;

  const updateHeight = () => {
    swiper.wrapperEl.style.height = `${fullHeight.value}px`;
  };

  if (timeout > 0) {
    setTimeout(updateHeight, timeout);
  } else {
    requestAnimationFrame(updateHeight);
  }
}

// Optimized navigation handler
function handleNavigationEvent(type: string, direction?: string) {
  const handlers: Record<string, any> = {
    next: {
      section: nextSlide,
      question: () =>
        sectionElements.value[currentSectionIdx.value - 1]?.nextSlide?.(),
    },
    previous: {
      section: previousSlide,
      'section-last-question': () => {
        previousSlide();
        setTimeout(() => {
          const sectionComponent =
            sectionElements.value[currentSectionIdx.value - 1];
          if (sectionComponent) {
            const lastIdx = (currentSection.value?.groups?.length || 0) - 1;
            sectionComponent.slideTo?.(lastIdx);
            updateQuestionIndex(lastIdx);
          }
        }, 300);
      },
      question: () =>
        sectionElements.value[currentSectionIdx.value - 1]?.previousSlide?.(),
    },
    skip: () => {
      if (currentSection.value?.id) {
        const visitedSections = mainStore.savePoints.life.visitedSections || [];
        if (!visitedSections.includes(currentSection.value.id)) {
          visitedSections.push(currentSection.value.id);
        }
      }
      nextSlide();
    },
    'goto-next-page': goToNextPage,
  };

  // Check if it's a direct handler or needs to access nested object
  if (typeof handlers[type] === 'function') {
    // Direct handler (like 'skip' or 'goto-next-page')
    handlers[type]();
  } else if (direction) {
    const nested = handlers[type]?.[direction];
    if (typeof nested === 'function') nested();
  } else {
    console.warn(`Unhandled navigation event: ${type} ${direction}`);
  }
}

// Other functions
const goHome = () => router.navigate('/evaluate', 'root', 'replace');

function setSectionRef(el: any) {
  if (el && !sectionElements.value.includes(el)) {
    sectionElements.value = [...sectionElements.value, el];
  }
}

function onHeaderSlide(e: number) {
  const currentSection = sectionElements.value[currentSectionIdx.value - 1];
  currentSection?.slideTo?.(e - 1);
  // Reset scroll position when navigating via header progress bar
  currentSection?.resetScroll?.();
  setTimeout(() => currentSection?.resetScroll?.(), 150);
}

function updateWarningState(state: boolean) {
  warningOpen.value = state;
}

// Provide values to child components
provide('currentSection', currentSection);
provide('colorScheme', colorScheme);
provide('nextSlide', nextSlide);
provide('previousSlide', previousSlide);
provide('setSection', setSection);
provide('goHome', goHome);
provide('goToNextPage', goToNextPage);
provide('sectionElementsRef', sectionElements);
provide('shouldResetNavigation', {
  shouldResetNavigation,
  setNavigationReset: () => (shouldResetNavigation.value = false),
});
provide('recalculateHeight', recalculateHeight);
provide('parentHeight', fullHeight);
provide('updateWarningState', updateWarningState);

// Swiper modules
const swiperModules = [IonicSlides];
</script>

<template>
  <ion-page class="no-scroll min-h-[100dvh] max-h-[100dvh]">
    <ion-content :fullscreen="true" class="test no-scroll" :scroll-y="false">
      <ion-fab
        slot="fixed"
        class="md:mr-[5%] lg:mt-[4%] md:mt-[6%] mt-[3%] lg:scale-100 md:scale-100 scale-75"
      >
        <a href="/evaluate">
          <ion-fab-button>
            <ion-icon :icon="homeSharp" />
          </ion-fab-button>
        </a>
      </ion-fab>

      <ion-loading
        :is-open="sections.length === 0"
        message="Laddar frågor..."
      />

      <min-livsstil-header
        :current-section-idx="currentSectionIdx"
        :current-page-idx="questionIndex"
        :question-count="questionCount"
        :sections="sections"
        :show-progress="currentSectionIdx <= sections.length"
        :is-results-page="currentSectionIdx > sections.length"
        @slide="onHeaderSlide"
        class="no-scroll"
      />

      <div class="swiper-container-wrapper no-scroll">
        <swiper
          class="main-swiper no-scroll !overflow-y-hidden !max-h-full"
          :modules="swiperModules"
          :slides-per-view="1"
          :allow-touch-move="false"
          @swiper="setSliderInstance"
        >
          <swiper-slide>
            <MinLivsstilStart
              :sections="sections"
              :is-widget="false"
              class="slider-content"
              @update-warning="updateWarningState"
            />
          </swiper-slide>

          <swiper-slide
            class="section-slide"
            v-for="section in sections"
            :key="section.id"
          >
            <MinLivsstilSection
              :data="section"
              :sections="sections"
              @index="updateQuestionIndex"
              @update-warning="updateWarningState"
              :ref="setSectionRef"
              class="slider-content"
            />
          </swiper-slide>

          <swiper-slide>
            <min-livsstil-results
              :sections="sections"
              class="slider-content"
              @update-warning="updateWarningState"
            />
          </swiper-slide>
        </swiper>
      </div>

      <!-- Unified Navigation Footer -->
      <unified-navigation
        :current-section-idx="currentSectionIdx"
        :sections-count="sections.length"
        :is-loading="isLoading"
        :question-index="questionIndex - 1"
        :question-count="questionCount"
        :warning-open="warningOpen"
        @next="handleNavigationEvent('next', $event)"
        @previous="handleNavigationEvent('previous', $event)"
        @skip="handleNavigationEvent('skip')"
        @goto-next-page="goToNextPage"
      />
    </ion-content>
  </ion-page>
</template>

<style scoped>
ion-fab {
  right: 0.5rem;
  top: 130px;
}

/* Remove scrollbar from main containers */
.no-scroll {
  overflow: hidden !important;
  -webkit-overflow-scrolling: none !important;
  touch-action: none !important;
}

/* Ensure ion-content doesn't scroll */
ion-content::part(scroll) {
  position: static !important;
  overflow: hidden !important;
  touch-action: none !important;
}

/* Swiper container shouldn't scroll */
.main-swiper {
  overflow: hidden !important;
  touch-action: none !important;
}

/* Allow only the slider content to scroll if needed */
.slider-content {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100%;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  padding-bottom: 80px; /* Add padding to account for the fixed footer */
}

/* Section slides styling */
.section-slide {
  overflow: hidden;
  touch-action: none;
}

/* Hide all scrollbars globally within this page */
:deep(*) {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
:deep(*::-webkit-scrollbar) {
  display: none;
}
</style>
